import React from "react";
import { CONSTANT_PAYMENT_METHODS_DICT } from "../../../constants";
import { useTranslation } from "react-i18next";

export const PrintTable = React.forwardRef(
    (
        {
            company,
            registerId,
            dateFrom,
            dateTo,
            uniqueTaxes,
            usedPaymentMethods,
            totals,
            firstReport,
            lastReport,
        },
        ref
    ) => {
        const { t } = useTranslation();

        const formatCurrency = (value) => {
            if (
                value === null ||
                value === undefined ||
                isNaN(parseFloat(value))
            ) {
                return "0,00";
            }
            try {
                return new Intl.NumberFormat("ro-MD", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(parseFloat(value));
            } catch (e) {
                return "0,00";
            }
        };

        const formatDate = (dateString) => {
            if (!dateString) return "";
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString("ro-MD", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                });
            } catch (e) {
                return dateString || "";
            }
        };

        const formatDateTime = (dateString) => {
            try {
                if (!dateString) return "";
                const parts = dateString.split("T");
                if (parts.length !== 2) return dateString;
                return `${formatDate(parts[0])} ${formatTime(parts[1])}`;
            } catch (e) {
                return dateString || "";
            }
        };

        const formatTime = (timeString) => {
            if (!timeString) return "";
            try {
                const time = new Date(`2000-01-01T${timeString}`);
                return time.toLocaleTimeString("ro-MD", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                });
            } catch (e) {
                return timeString || "";
            }
        };

        const getTaxDisplayName = (tax) => {
            if (!tax || !tax.code) return "Fără TVA";
            switch (tax.code) {
                case "A":
                    return `Cota A - ${tax.percent}%`;
                case "B":
                    return `Cota B - ${tax.percent}%`;
                case "C":
                    return `Cota C - ${tax.percent}%`;
                case "_":
                    return "Cota Fără TVA";
                default:
                    return `Cota ${tax.code} - ${tax.percent}%`;
            }
        };

        const getPaymentMethodName = (methodType) => {
            const paymentMethodName = CONSTANT_PAYMENT_METHODS_DICT[methodType]
                ? CONSTANT_PAYMENT_METHODS_DICT[methodType].methodName
                : methodType;
            return t(paymentMethodName) || paymentMethodName;
        };

        const selectedCash = company?.cash_registers?.find(
            (cash) => cash._id === Number.parseInt(registerId)
        );

        const createdDate = selectedCash?.changes?.created;
        const updatedDate = selectedCash?.changes?.mev_auth_changes[0]?.updated;

        const getTaxUpdatedDate = (tax) => {
            const taxChanges = selectedCash?.changes?.tax_changes;
            if (!tax || !tax.code || !tax.percent) return "";

            const taxChange = taxChanges?.find(
                (item) =>
                    item.tax.code === tax.code &&
                    item.tax.percent === parseInt(tax.percent)
            );

            return taxChange?.updated ? formatDateTime(taxChange.updated) : "-";
        };

        const styles = {
            container: {
                padding: "10px",
                fontFamily: "Arial, sans-serif",
                fontSize: "9px",
                maxWidth: "100%",
                lineHeight: "1.2",
            },
            header: {
                textAlign: "center",
                marginBottom: "10px",
            },
            title: {
                fontSize: "12px",
                margin: "0 0 2px 0",
                fontWeight: "bold",
            },
            period: {
                margin: "0",
                fontSize: "9px",
            },
            section: {
                marginBottom: "8px",
            },
            sectionTitle: {
                fontSize: "10px",
                borderBottom: "1px solid #ccc",
                paddingBottom: "2px",
                marginTop: "6px",
                marginBottom: "4px",
                fontWeight: "bold",
            },
            table: {
                width: "100%",
                borderCollapse: "collapse",
            },
            tableBordered: {
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #ccc",
            },
            labelCell: {
                width: "30%",
                padding: "1px 0",
                verticalAlign: "top",
                fontSize: "9px",
                color: "#555",
            },
            valueCell: {
                padding: "1px 0",
                verticalAlign: "top",
                fontSize: "9px",
            },
            tableHeader: {
                backgroundColor: "#f2f2f2",
                fontSize: "9px",
            },
            tableHeaderCell: {
                padding: "2px",
                textAlign: "left",
                border: "1px solid #ccc",
                fontWeight: "bold",
            },
            tableHeaderCellRight: {
                padding: "2px",
                textAlign: "right",
                border: "1px solid #ccc",
                fontWeight: "bold",
            },
            tableCell: {
                padding: "2px",
                border: "1px solid #ccc",
                fontSize: "9px",
            },
            tableCellRight: {
                padding: "2px",
                textAlign: "right",
                border: "1px solid #ccc",
                fontSize: "9px",
            },
            totalRow: {
                backgroundColor: "#f2f2f2",
                fontWeight: "bold",
            },
            footer: {
                marginTop: "10px",
                fontSize: "8px",
                color: "#666",
                textAlign: "center",
            },
        };

        return (
            <div ref={ref} style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Dare de seamă periodică</h2>
                    <p style={styles.period}>
                        Perioada: {formatDate(dateFrom)} - {formatDate(dateTo)}
                    </p>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Informații Companie</h3>
                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={styles.labelCell}>Organizația:</td>
                                <td style={styles.valueCell}>
                                    {company?.name || "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>Cod fiscal:</td>
                                <td style={styles.valueCell}>
                                    {company?.idno || "-"}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Datele despre EKassa</h3>
                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={styles.labelCell}>Nr. de serie:</td>
                                <td style={styles.valueCell}>
                                    {selectedCash?.factory || "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>creat la data:</td>
                                <td style={styles.valueCell}>
                                    {createdDate
                                        ? formatDateTime(createdDate)
                                        : "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>
                                    Nr. de înregistrare:
                                </td>
                                <td style={styles.valueCell}>
                                    {selectedCash?.number || "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>
                                    fiscalizat la data:
                                </td>
                                <td style={styles.valueCell}>
                                    {updatedDate
                                        ? formatDateTime(updatedDate)
                                        : "-"}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Detalii Rapoarte</h3>
                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={styles.labelCell}>
                                    Primul raport Z:
                                </td>
                                <td style={styles.valueCell}>
                                    {firstReport?.soap_data?.report || "-"} din{" "}
                                    {firstReport?.soap_data?.datetime
                                        ? formatDateTime(
                                              firstReport.soap_data.datetime
                                          )
                                        : "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>
                                    Ultimul raport Z:
                                </td>
                                <td style={styles.valueCell}>
                                    {lastReport?.soap_data?.report || "-"} din{" "}
                                    {lastReport?.soap_data?.datetime
                                        ? formatDateTime(
                                              lastReport.soap_data.datetime
                                          )
                                        : "-"}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.labelCell}>
                                    Cantitatea bonurilor:
                                </td>
                                <td style={styles.valueCell}>
                                    {totals?.receipts || 0}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Cote TVA</h3>
                    <table style={styles.table}>
                        <tbody>
                            {uniqueTaxes.map((tax, index) => (
                                <tr key={`tax-date-${index}`}>
                                    <td style={styles.labelCell}>
                                        {getTaxDisplayName(tax)}
                                    </td>
                                    <td style={styles.valueCell}>
                                        {getTaxUpdatedDate(tax)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Vînzări</h3>
                    <table style={styles.tableBordered}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th
                                    style={{
                                        ...styles.tableHeaderCell,
                                        width: "60%",
                                    }}
                                >
                                    Categorie
                                </th>
                                <th
                                    style={{
                                        ...styles.tableHeaderCellRight,
                                        width: "20%",
                                    }}
                                >
                                    BRUT
                                </th>
                                <th
                                    style={{
                                        ...styles.tableHeaderCellRight,
                                        width: "20%",
                                    }}
                                >
                                    Valoare TVA
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {uniqueTaxes.map((tax, index) => {
                                const key = `${tax.code}-${tax.percent}`;
                                return (
                                    <tr key={`tax-row-${index}`}>
                                        <td style={styles.tableCell}>
                                            {getTaxDisplayName(tax)}
                                        </td>
                                        <td style={styles.tableCellRight}>
                                            {formatCurrency(
                                                totals?.[`${key}-total`] || 0
                                            )}
                                        </td>
                                        <td style={styles.tableCellRight}>
                                            {formatCurrency(
                                                totals?.[`${key}-tva`] || 0
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr style={styles.totalRow}>
                                <td
                                    style={{
                                        ...styles.tableCell,
                                        fontWeight: "bold",
                                    }}
                                >
                                    TOTAL
                                </td>
                                <td
                                    style={{
                                        ...styles.tableCellRight,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {formatCurrency(
                                        uniqueTaxes.reduce((sum, tax) => {
                                            const key = `${tax.code}-${tax.percent}`;
                                            return (
                                                sum +
                                                parseFloat(
                                                    totals?.[`${key}-total`] ||
                                                        0
                                                )
                                            );
                                        }, 0)
                                    )}
                                </td>
                                <td
                                    style={{
                                        ...styles.tableCellRight,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {formatCurrency(
                                        uniqueTaxes.reduce((sum, tax) => {
                                            const key = `${tax.code}-${tax.percent}`;
                                            return (
                                                sum +
                                                parseFloat(
                                                    totals?.[`${key}-tva`] || 0
                                                )
                                            );
                                        }, 0)
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Metode de plată</h3>
                    <table style={styles.tableBordered}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th
                                    style={{
                                        ...styles.tableHeaderCell,
                                        width: "70%",
                                    }}
                                >
                                    Metodă
                                </th>
                                <th
                                    style={{
                                        ...styles.tableHeaderCellRight,
                                        width: "30%",
                                    }}
                                >
                                    Sumă
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {usedPaymentMethods
                                .filter((method) => {
                                    const key = `payment-${method.type}`;
                                    return parseFloat(totals?.[key] || 0) !== 0;
                                })
                                .map((method, index) => {
                                    const key = `payment-${method.type}`;
                                    return (
                                        <tr key={`payment-row-${index}`}>
                                            <td style={styles.tableCell}>
                                                {getPaymentMethodName(method.type)}
                                            </td>
                                            <td style={styles.tableCellRight}>
                                                {formatCurrency(totals?.[key] || 0)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            <tr style={styles.totalRow}>
                                <td
                                    style={{
                                        ...styles.tableCell,
                                        fontWeight: "bold",
                                    }}
                                >
                                    TOTAL
                                </td>
                                <td
                                    style={{
                                        ...styles.tableCellRight,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {formatCurrency(
                                        Object.keys(totals || {})
                                            .filter(key => key.startsWith("payment-"))
                                            .reduce(
                                                (sum, key) => sum + parseFloat(totals[key] || 0),
                                                0
                                            )
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={styles.footer}>
                    Document generat la{" "}
                    {formatDateTime(new Date().toISOString())}
                </div>
            </div>
        );
    }
);
